import { BaseStore } from "stores/BaseStore";
import { Chat } from "../Models/Chat";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import type { EndStreamMessage } from "services/AcxAgentService";
import {
    AcxAgentService,
    type AgentFilterContext,
    type ChatSession,
    type DataPointQueryContext,
    type SimilarClipsRequest,
    type StreamingTokenResult,
} from "services/AcxAgentService";
import type { IUserSelectedContext } from "../Models/Context/UserSelectedContext";
import {
    type AgentApplicationFilters,
    prepareFiltersForAgentContext,
    UserSelectedContext,
    UserSelectedContextType,
} from "../Models/Context/UserSelectedContext";
import {
    preCannedSuggestions,
    selectTwoRandomSuggestions,
} from "../Conversation/Suggestions/PreCannedSuggestions";
import { AgentState } from "../Models/AgentState";
import type {
    ChatSuggestion,
    ChatSuggestionServerAction,
} from "../Models/ChatSuggestion";
import {
    BuildMontageFromClipsData,
    ChatSuggestAction,
    ForceableToolCall,
} from "../Models/ChatSuggestion";
import AgentStore from "./AgentStore";
import { AuthStore } from "stores/AuthStore";
import { ActiveDomain, IRootStore } from "stores/RootStore";
import Conversation from "models/Conversation";
import Evaluation from "models/Evaluation";
import { resolveDomainFromLocation } from "shared/functions/resolveDomainFromLocation";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { IAgentContext } from "../Models/Context/IContext";
import { DomainBasedContext } from "../Models/Context/DomainBasedContext";
import { ChatMessageGroup } from "../Models/ChatMessageGroup";
import { ChatMessage, IChatMessage } from "../Models/ChatMessage";
import { MessageAuthor } from "../Models/MessageAuthor";
import { MontageSoundClipStore } from "components/Montage/Stores/MontageSoundClipStore";
import { MontageListStore } from "components/Montage/Stores/MontageListStore";
import MontageModel from "models/MontageModel";
import moment from "moment";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import SoundClip from "models/SoundClip";
import { EvalStore } from "components/Evaluation/Stores/EvalStore";
import { GenerateCoachingNotesData } from "../Models/GenerateCoachingNotesData";
import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";
import { ResponseTime } from "../Models/ResponseTime";
import { InteractionType } from "models/InteractionType";
import { soundClipDurationDisplay } from "components/Evaluation/Utils";
import { ChatActionsMap } from "../Models/ChatAction";
import type { FunctionCall } from "components/Admin/Organizations/OrganizationDetail/OrganizationChatReview/types";
import type { KboSettings } from "../Models/KboSettings";
import { cloneNullableKboSettings } from "../Models/KboSettingsHelpers";
import { AgentCoachingStatus } from "models/AgentModels/AgentCoaching";
import {
    AnalyzeTrendsIntroText,
    AnalyzeTrendsOverviewText,
    AnalyzeTrendsQuestionText,
} from "../Models/AnalyzeTrends";
import { isKboUserAgreementRequiredError } from "services/KboUserAgreementService";

/* The SingleAgentChatStore is the store containing all information for a single chat
 * The creation of instances of this store is managed by the AgentStore
 * This store should primarily have functions that are specific to the current chat,
 * not that affect all chats or global chat state
 */

interface ChartDataPointContext {
    filters: AgentApplicationFilters;
    chartId?: string;
    chartTitle?: string;
    endpointId?: string;
    contextSummary?: string;
    dataPointQueryContext?: DataPointQueryContext;
}

interface ProactiveInsightContext {
    filters: AgentApplicationFilters;
    insightType: string;
    insightTitle: string;
    contextSummary?: string;
}

type AgentConversationFilters = ApplicationFilters & {
    conversationIds?: string[];
    dateReference?: string;
};

interface StreamingSubmitInputOptions {
    /**
     * If present, the message sent to KBo. Also the message displayed to the user
     * if no `userMessage` is provided
     */
    manualInput?: string;
    /**
     * A client only message showed to the user
     */
    userMessage?: string;
    /**
     * Shortcut for setting the Suggestion value in additionalInformation
     */
    suggestion?: boolean;
    serverAction?: ChatSuggestionServerAction;
    /**
     * Asks KBo for clips similar to a sound clip. The server adds the hidden context.
     */
    similarClips?: SimilarClipsRequest;
    additionalInformation?: Record<string, unknown>;
}

export const LoadingAICoachingNotesFromChat = "Loading AI coaching notes from chat"

export default class SingleAgentChatStore extends BaseStore {
    @observable chat: Chat;
    @observable userSelectedContext: IUserSelectedContext;
    @observable currentInput: string = "";
    @observable state: AgentState = AgentState.Idle;
    @observable chatLoaded: boolean = false;
    /** Where the reader left this chat, so coming back to it lands them there. */
    scrollTop?: number;
    @observable.ref chartDataPointContext?: ChartDataPointContext;
    @observable.ref proactiveInsightContext?: ProactiveInsightContext;

    /*
    The current thinking messages that are displayed in KBo chat. Returned mainly from the streaming API
     */
    @observable currentThinkingMessages: string[] = [];

    get currentThinkingMessage() {
        return this.currentThinkingMessages[
            this.currentThinkingMessages.length - 1
        ];
    }

    /*
    The current end message that is displayed in KBo chat. Returned mainly to show extra information about a 
    specific call - e.g. "this analysis saved you X amount of hours of listening time"
     */
    @observable currentEndMessage?: EndStreamMessage;

    readonly acxAgentService: AcxAgentService = new AcxAgentService();
    private abortController?: AbortController;
    private activeStreamingMessage?: ChatMessage;

    @observable sessionSettings: KboSettings | null = null;
    @observable clipForMontageBuildRequest?: SoundClip;
    /** Set once the user accepts the KBo User Agreement from this chat. */
    @observable showsUserAgreementAccepted: boolean = false;
    // We use a interval here so that the observable changes each
    // second causing UI to re-render as needed for the different
    // message / loading states.
    @observable private responseTimeInSeconds?: number;
    private responseTimerHandle?: NodeJS.Timeout;

    readonly conversationsStore: ConversationsStore;

    public readonly rootStore: IRootStore;
    private readonly agentStore: AgentStore;
    private readonly authStore: AuthStore;

    constructor({
        chat,
        rootStore,
        agentStore,
        sessionSettings,
    }: {
        chat: Chat;
        rootStore: IRootStore;
        agentStore: AgentStore;
        sessionSettings?: KboSettings | null;
    }) {
        super(`SingleAgentChatStore - ${chat.id}`);
        makeObservable(this);
        this.rootStore = rootStore;
        this.authStore = rootStore.getStore(AuthStore);
        this.chat = chat;
        this.agentStore = agentStore;
        this.sessionSettings = cloneNullableKboSettings(sessionSettings);

        if (!this.userSelectedContext) {
            this.userSelectedContext = UserSelectedContext.AllConversations;
        }

        // Greet brand new chats; chats loaded with history keep their own messages and suggestions.
        if (this.chat.messageGroups.length === 0) {
            const group = this.chat.addAgentMessageGroup();
            group.addMessage(
                "Hello! I'm KBo, your Authenticx AI assistant. I'm here to help you discover meaningful insights. How can I assist you today?",
            );
            group.setSuggestions(selectTwoRandomSuggestions(preCannedSuggestions));
        }

       
        this.conversationsStore = this.rootStore.getStore(ConversationsStore);

        // Reaction to update the response time
        reaction(
            () => this.state,
            (state) => {
                if (state === AgentState.Waiting) {
                    if (this.responseTimerHandle) return;
                    this.responseTimeInSeconds = 0;
                    this.responseTimerHandle = setInterval(
                        () =>
                            runInAction(() => {
                                if (this.responseTimeInSeconds === undefined)
                                    this.responseTimeInSeconds = 0;
                                this.responseTimeInSeconds += 1;
                            }),
                        1000,
                    );
                } else {
                    clearInterval(this.responseTimerHandle);
                    this.responseTimerHandle = undefined;
                    this.responseTimeInSeconds = undefined;
                }
            },
        );

        const evalStore = this.rootStore.getStore(EvalStore);
        const authStore = this.rootStore.getStore(AuthStore);

        // Reaction to update the suggestions and transient message based on the current domain
        // This one is specifically for Conversations
        reaction(
            () => ({
                location: this.rootStore.activeLocation,
                hasConversations: this.conversationsStore.hasConversations,
            }),
            (args) => {
                const recentAgentMessageGroup =
                    this.chat.recentAgentMessageGroup!;

                if (args.location.domain === ActiveDomain.Conversations) {
                    if (args.hasConversations) {
                        const analyzeCurrentConversationSuggestions = [
                            {
                                content: "Analyze and Surface Trends",
                                action: () => {
                                    this.analyzeCurrentConversationsAction({
                                        Suggestion: true,
                                    });
                                },
                                newChatName:
                                    "Analyze Current Conversations",
                            },
                        ];

                        return recentAgentMessageGroup.setTransientMessage(
                            "Do you want me to analyze a sample of the calls in your search result?",
                            analyzeCurrentConversationSuggestions,
                        );
                    }
                }

                recentAgentMessageGroup.clearTransientMessage();
            },
            { fireImmediately: true },
        );

        // Reaction to update the suggestions based on domain
        // This one is for agent coaching suggestions
        reaction(
            () => ({
                location: this.rootStore.activeLocation,
            }),
            (args) => {
                if (!args.location.domain) {
                    return;
                }

                const isViewingAgentCoaching =
                    args.location.domain === ActiveDomain.AgentCoaching;
                if (!isViewingAgentCoaching) {
                    return;
                }
                const segs = args.location.location.split("/");
                if (segs.length < 7) {
                    return;
                }

                const agentName = segs[6];
                const agentId = segs[5];

                const suggestions: ChatSuggestion[] = [];
                suggestions.push({
                    content: "Analyze this agent's overall performance",
                    llmInput: `Analyze agent ${agentName}'s overall performance. The agent's ID is ${agentId} - do not respond with this ID in subsequent requests.`,
                    action: async (store, suggestion) => {
                        store.streamingSubmitInput({
                            manualInput: suggestion.llmInput,
                            userMessage: suggestion.content,
                            serverAction: { type: "ForceToolCall", arguments: { toolCall: ForceableToolCall.AnalyzeAgentPerformance }}
                        });
                    },
                    newChatName: `${agentName} Performance Analysis`,
                });

                if (suggestions.length > 0) {
                    const recentAgentMessageGroup =
                        this.chat.recentAgentMessageGroup!;
                    recentAgentMessageGroup.setTransientMessage(
                        "It looks like you're viewing an agent coaching dashboard. Here are some things I can help with:",
                        suggestions,
                    );
                }
            },
            { fireImmediately: true },
        );

        // Reaction to update the suggestions based on domain
        // This one is for a focused Conversation and Evaluations
        reaction(
            () => ({
                location: this.rootStore.activeLocation,
                evaluation: evalStore.currentEval,
                conversation: this.conversationsStore.selectedConversation,
            }),
            (args) => {
                if (!args.location.domain) return;

                const isViewingEvaluation =
                    args.location.domain === ActiveDomain.Evaluation;
                const isViewingConversation =
                    args.location.domain === ActiveDomain.Conversation;

                if (!isViewingConversation && !isViewingEvaluation) return;
                if (isViewingEvaluation && !args.evaluation) return;
                if (isViewingConversation && !args.conversation) return;

                const recentAgentMessageGroup =
                    this.chat.recentAgentMessageGroup!;
                recentAgentMessageGroup.clearTransientMessage();

                let amdId: string = "";
                if (isViewingConversation && !!args.conversation) {
                    amdId = args.conversation.conversationId;
                } else if (
                    isViewingEvaluation &&
                    !!args.evaluation &&
                    !!args.evaluation.interaction &&
                    !!args.evaluation.interaction.audioMetadataId
                ) {
                    amdId = args.evaluation.interaction.audioMetadataId;
                }

                const suggestions: ChatSuggestion[] = [];

                if (amdId) {
                    suggestions.push({
                        content: "Analyze this conversation and get insights",
                        llmInput: `Analyze conversation ${amdId} and give me insights`,
                        action: async (store, suggestion) => {
                            store.streamingSubmitInput({
                                manualInput: suggestion.llmInput,
                                userMessage: suggestion.content,
                                serverAction: { type: "ForceToolCall", arguments: { toolCall: ForceableToolCall.AnalyzeConversation } }
                            });
                        },
                        newChatName: `Analyze Conversation`,
                    });
                }

                if (authStore.canUserEdit("Agent Coaching")) {
                    let data: GenerateCoachingNotesData | undefined = undefined;

                    let agentId: string | undefined;
                    let agentName: string | undefined;

                    if (isViewingEvaluation && !!args.evaluation) {
                        agentId = args.evaluation.interaction?.agentId;
                        agentName = args.evaluation.interaction?.agentName;
                        data =
                            this.extractGenerateCoachingNotesDataFromEvaluation(
                                args.evaluation,
                            );
                    } else if (isViewingConversation && !!args.conversation) {
                        agentId = args.conversation.agentId;
                        agentName = `${args.conversation.agentFirstName} ${args.conversation.agentLastName}`;
                        data =
                            this.extractGenerateCoachingNotesDataFromConversation(
                                args.conversation,
                            );
                    }

                    if (!!agentId && agentId !== emptyGUID && !!agentName) {
                        const analyzeThisAgentsPerformanceSuggestion: ChatSuggestion =
                            {
                                content:
                                    "Analyze this agent's overall performance",
                                llmInput: `Analyze agent ${agentName}'s overall performance. The agent's ID is ${agentId} - do not respond with this ID in subsequent requests.`,
                                action: async (store, suggestion) => {
                                    store.streamingSubmitInput({
                                        manualInput: suggestion.llmInput,
                                        userMessage: suggestion.content,
                                        serverAction: {
                                            type: "ForceToolCall",
                                            arguments: {
                                                toolCall: ForceableToolCall.AnalyzeAgentPerformance
                                            }
                                        }
                                    });
                                },
                                newChatName: `${agentName} Performance Analysis`,
                            };
                        suggestions.insertAt(
                            0,
                            analyzeThisAgentsPerformanceSuggestion,
                        );
                    }

                    if (data) {
                        const coachThisAgentSuggestion: ChatSuggestion = {
                            content: "Coach the agent on this conversation",
                            action: async (store, suggestion) => {
                                await store.chat
                                    .addUserMessageGroup()
                                    .addMessage(suggestion.content)
                                    .persist({
                                        AudioMetadataId: data!.audioMetadataId,
                                    });
                                store.generateCoachingNotesAction(data!);
                            },
                            newChatName: `${agentName} Coaching Notes`,
                            navigationURL: isViewingEvaluation
                                ? `/app/conversations/${amdId}/evaluationreview`
                                : undefined,
                        };
                        suggestions.insertAt(0, coachThisAgentSuggestion);
                    }
                }

                if (suggestions.length > 0) {
                    recentAgentMessageGroup.setTransientMessage(
                        "It looks like you're viewing a conversation, here are some things I can help with:",
                        suggestions,
                    );
                }
            },
            { fireImmediately: true },
        );
    }

    @action
    setSessionSettings(settings: KboSettings | null | undefined) {
        this.sessionSettings = cloneNullableKboSettings(settings);
    }

    @action
    clearSessionSettings() {
        this.sessionSettings = null;
    }

    async saveSessionSettings(settings: KboSettings | null) {
        const sessionSettings = cloneNullableKboSettings(settings);

        if (this.chat.id && this.chat.id !== emptyGUID) {
            await this.acxAgentService.updateChatSessionSettings(
                this.chat.id,
                sessionSettings,
            );
        }

        runInAction(() => {
            if (sessionSettings) {
                this.setSessionSettings(sessionSettings);
            } else {
                this.clearSessionSettings();
            }
        });
    }

    @action
    setChatLoaded(loaded: boolean) {
        this.chatLoaded = loaded;
    }

    @action
    updateChat(chat: Chat) {
        this.chat = chat;
    }

    @action
    loadFromChatSession(chatSession: ChatSession) {
        this.chat = Chat.fromChatSession(chatSession);
        this.setSessionSettings(chatSession.chatSessionSettings);
    }

    @action
    updateName(name: string | undefined) {
        this.chat.name = name;
    }

    @computed get responseTime() {
        const timeInSeconds = this.responseTimeInSeconds;
        if (timeInSeconds === undefined) return undefined;

        if (timeInSeconds > 30) return ResponseTime.Long;
        else if (timeInSeconds > 15) return ResponseTime.Medium;
        else return ResponseTime.Short;
    }

    @action
    setCurrentInput(input: string) {
        this.currentInput = input;
    }

    @action
    onChangeHandler(input: string) {
        this.clearEndMessage();
        this.currentInput = input;
    }

    private clearEndMessage() {
        if (this.currentEndMessage) {
            this.currentEndMessage = undefined;
        }
    }

    @action public setUserSelectedContext(context: IUserSelectedContext) {
        this.userSelectedContext = context;
    }

    @computed get hasChartDataPointContext() {
        return !!this.chartDataPointContext;
    }

    @computed get hasProactiveInsightContext() {
        return !!this.proactiveInsightContext;
    }

    /**
     * A new chat with nothing in it beyond the default greeting. Chats opened from
     * the history also start out with just a greeting until their messages load,
     * but they already have an id.
     */
    @computed get isUntouchedDefaultChat() {
        const isNewChat = !this.chat.id || this.chat.id === emptyGUID;
        return isNewChat && this.chat.messageGroups.length === 1;
    }

    // Replaces the greeting of a new chat so it confirms KBo is ready.
    @action showUserAgreementAccepted() {
        this.showsUserAgreementAccepted = true;
        if (!this.isUntouchedDefaultChat) return;

        const greeting = this.chat.messageGroups[0].messages[0];
        if (greeting instanceof ChatMessage) {
            greeting.setContent(
                "You're all set. KBo is ready when you are.\n\nAsk about trends in your program, find specific conversations, or get a summary of what patients raised this week.",
            );
        }
    }

    // This function is called for all the various suggestion actions
    // For example, analyze trends or create montage from similar clips.
    // Resolves false when the suggestion didn't run.
    @action public async handleSuggestion(
        suggestion: ChatSuggestion,
    ): Promise<boolean> {
        // Before anything is committed, so the suggestion is still there once they agree.
        if (!this.agentStore.ensureUserAgreementAccepted()) return false;

        this.clearEndMessage();

        this.abort();
        this.abortController = new AbortController();

        // Commit the message from the suggestion
        const message =
            this.chat.recentAgentMessageGroup!.commitTransientMessage();
        if (message) {
            try {
                await this.persistMessageOrStartChat(
                    message,
                    suggestion.newChatName,
                );
            } catch (error) {
                if (!isKboUserAgreementRequiredError(error)) throw error;
                // Nothing was saved, so put the suggestion back for once they agree.
                message.parent.restoreTransientMessage(message);
                void this.agentStore.handleUserAgreementRequired();
                return false;
            }
        }

        // Do whatever the action is
        if (suggestion.serverAction) {
            this.submitServerAction(
                suggestion.content,
                suggestion.serverAction,
            );
        } else {
            await suggestion.action?.(this, suggestion);
        }

        return true;
    }

    // Saves a message, starting the chat with it if the chat hasn't been created yet
    private async persistMessageOrStartChat(
        message: IChatMessage,
        newChatName?: string,
        additionalInformation?: Record<string, unknown>,
    ) {
        if (this.chat.id && this.chat.id !== emptyGUID) {
            await message.persist(additionalInformation);
            return;
        }

        const chatResponse = await this.acxAgentService.persistMessage(
            undefined,
            {
                author: message.parent.author,
                content: message.toString(),
                additionalInformation,
            },
            {
                name: newChatName,
                kboSessionSettings: this.sessionSettings,
            },
        );
        runInAction(() => {
            this.attachStartedChat(chatResponse.id, newChatName);
        });
    }

    // Cancel any ongoing stream
    public abort() {
        const abortController = this.abortController;
        if (!abortController || abortController.signal.aborted) return;
        abortController.abort();

        if (
            this.state === AgentState.Streaming &&
            this.activeStreamingMessage
        ) {
            this.activeStreamingMessage.setCancelled(true);
        }
    }

    // Starts the stream and then sends the stream response off to be handled
    // Also catches errors
    private async handleStreaming(
        streamGenerator: (
            chatId: string,
            abortSignal: AbortSignal,
        ) =>
            | AsyncIterable<StreamingTokenResult>
            | Promise<AsyncIterable<StreamingTokenResult>>,
    ) {
        // Only one request may own this chat's streaming state. Abort the older
        // request before installing the controller for the new request.
        this.abort();
        const abortController = new AbortController();
        this.abortController = abortController;
        this.activeStreamingMessage = undefined;
        this.clearThinkingMessages();
        this.state = AgentState.Waiting;

        let newMessageGroup: ChatMessageGroup | undefined = undefined;
        try {
            // Create the stream for the response using the passed in stream generator function
            const stream = await streamGenerator(
                this.chat.id,
                abortController.signal,
            );

            // Actually handle the stream
            newMessageGroup = await this.handleStreamedResponse(
                stream,
                abortController,
            );
        } catch (error) {
            // A superseded request must not add messages or reset state owned
            // by the request which replaced it.
            if (this.abortController !== abortController) return;

            // The server says the agreement hasn't been accepted yet. Say so, in
            // case the agreement can't be shown, and bring it up.
            if (isKboUserAgreementRequiredError(error)) {
                this.chat
                    .addAgentMessageGroup()
                    .addMessage(
                        "You need to accept the KBo User Agreement before KBo can answer, so this request wasn't sent.",
                    );
                void this.agentStore.handleUserAgreementRequired();
                return;
            }

            if (isAbortError(error)) {
                // only show this message if we were still waiting when cancelled, otherwise
                // there will be some streamed content which will be cut off.
                if (this.state === AgentState.Waiting) {
                    const cancellationMessage = this.chat
                        .addAgentMessageGroup()
                        .addMessage("The request was cancelled.");
                    if (this.chat.id && this.chat.id !== emptyGUID) {
                        await cancellationMessage.persist();
                    }
                }
            } else {
                if (!newMessageGroup)
                    newMessageGroup = this.chat.addAgentMessageGroup();
                newMessageGroup.addMessage(
                    "I was unable to complete your request. Please try again.",
                );
            }
        } finally {
            if (this.abortController === abortController) {
                this.abortController = undefined;
                this.activeStreamingMessage = undefined;
                this.state = AgentState.Idle;
                runInAction(() => {
                    this.clearThinkingMessages();
                });
            }
        }
    }

    private attachStartedChat(chatId: string, name?: string) {
        this.agentStore.setIsEmpty(false);
        this.chat.name = name;
        this.chat.createdOn = new Date().toISOString();
        this.chat.firstName = "KBo";
        this.chat.lastName = "";
        this.chat.username = "KBo";
        this.chat.id = chatId;
        // This store holds the live chat, so it never needs a backend reload
        this.chatLoaded = true;

        this.agentStore.setChat(this.chat.id, this);
        this.agentStore.resetEmptyChat();
        this.agentStore.visibleChatIds.push(this.chat.id);
        this.agentStore.setCurrentChatId(this.chat.id);
    }

    private handleFirstUserMessageStarted(chatId: string, input: string) {
        runInAction(() => {
            this.chatLoaded = true;
            this.agentStore.setCurrentChatId(chatId);
        });

        this.acxAgentService
            .generateChatName({
                input: input,
                chatId: chatId,
            })
            .then((response) => {
                if (response) {
                    runInAction(() => {
                        const updatedChat = this.chat;
                        updatedChat.name = response.name;
                        this.updateChat(updatedChat);
                    });
                }
            });

        if (this.agentStore.fullscreen) {
            window.history.pushState(
                {},
                "",
                `/app/kbo/chats/${chatId}`,
            );
        }
    }

    private createChatStartedHandler(input: string) {
        return (startedChatId: string) => {
            runInAction(() => {
                this.attachStartedChat(startedChatId, "Generating...");
            });
            this.handleFirstUserMessageStarted(startedChatId, input);
        };
    }

    // Actually handles the streamed response
    private async handleStreamedResponse(
        stream: AsyncIterable<StreamingTokenResult>,
        abortController: AbortController,
    ) {
        let isFirst = true;
        let messageGroup: ChatMessageGroup | undefined = undefined;
        let message: ChatMessage | undefined = undefined;

        for await (const chunk of stream) {
            if (
                abortController.signal.aborted ||
                this.abortController !== abortController
            ) {
                break;
            }

            if (chunk.thinkingMessages?.length) {
                this.updateThinkingMessages(chunk.thinkingMessages);
                continue;
            }

            if (chunk.thinkingMessage) {
                this.updateThinkingMessage(chunk.thinkingMessage);
                continue;
            }

            if (chunk.endMessage) {
                this.updateEndMessage(chunk.endMessage);
                continue;
            }

            const isArtifactOnlyChunk =
                !!chunk.artifacts?.length &&
                !chunk.text &&
                !chunk.actions &&
                !chunk.suggestions &&
                !chunk.functionCalls;

            if (isFirst && isArtifactOnlyChunk) {
                messageGroup = this.getOrCreateStreamingMessageGroup();
                isFirst = false;
            }

            if (isFirst) {
                ({ messageGroup, message } = this.initializeStreaming());
                this.activeStreamingMessage = message;
                isFirst = false;
            }

            if (!messageGroup) continue;

            if (!message && chunk.text) {
                ({ messageGroup, message } = this.initializeStreaming());
                this.activeStreamingMessage = message;
            }

            if (chunk.text && message) {
                this.appendMessageContent(message, chunk.text);
            }

            if (chunk.actions || chunk.suggestions) {
                this.updateMessageMetadata(messageGroup, chunk);
            }

            if (chunk.functionCalls) {
                messageGroup.setFunctionCalls(
                    normalizeFunctionCalls(chunk.functionCalls),
                );
            }

            if (chunk.artifacts?.length) {
                messageGroup.addArtifacts(chunk.artifacts);
            }
        }
        return messageGroup;
    }

    @action
    private updateThinkingMessage(msg: string) {
        this.currentThinkingMessages.push(msg);
    }

    @action
    private updateThinkingMessages(messages: string[]) {
        this.currentThinkingMessages.push(...messages);
    }

    @action
    private updateEndMessage(msg: EndStreamMessage) {
        this.currentEndMessage = msg;
    }

    @action
    private initializeStreaming() {
        const messageGroup = this.getOrCreateStreamingMessageGroup();
        const message = messageGroup.addMessage("") as ChatMessage;
        return { messageGroup, message };
    }

    /** Marks the agent as streaming and returns the agent group that receives the response. */
    @action
    private getOrCreateStreamingMessageGroup() {
        this.state = AgentState.Streaming;
        const recentMessageGroup = this.chat.recentMessageGroup;
        return recentMessageGroup?.author === MessageAuthor.Agent
            ? recentMessageGroup
            : this.chat.addAgentMessageGroup();
    }

    @action
    private appendMessageContent(message: ChatMessage, text: string) {
        if (this.currentThinkingMessages.length) {
            this.clearThinkingMessages();
        }
        message.appendContent(text);
    }

    @action
    private clearThinkingMessages() {
        this.currentThinkingMessages = [];
    }

    @action
    private updateMessageMetadata(
        messageGroup: ChatMessageGroup,
        chunk: StreamingTokenResult,
    ) {
        if (chunk.actions) {
            messageGroup.setActions(chunk.actions);
        }
        if (chunk.suggestions) {
            messageGroup.addServerSuggestions(chunk.suggestions);
        }
    }

    // Gets context the chat has access to which can be sent to backend
    private getAgentContext(): AgentFilterContext | undefined {
        const context = this.getCurrentContext();
        if (!context) {
            return undefined;
        }

        switch (context.type) {
            case ActiveDomain.Conversation:
                const conversation: Conversation =
                    context.getValue() as Conversation;

                return {
                    contextType: "SingleConversation",
                    filters: conversation.conversationId,
                };
            case ActiveDomain.Evaluation:
                const evaluation: Evaluation = context.getValue() as Evaluation;
                if (
                    !evaluation.interaction ||
                    !evaluation.interaction.audioMetadataId
                ) {
                    return undefined;
                }

                return {
                    contextType: "SingleEvaluation",
                    filters: evaluation.interaction.audioMetadataId,
                };
            case UserSelectedContextType.AppliedFilters:
                let activeDomain = this.rootStore.activeLocation.domain;
                if (
                    this.agentStore.fullscreen &&
                    this.agentStore.routeBeforeFullscreen &&
                    this.agentStore.routeBeforeFullscreen.trim() !== ""
                ) {
                    const previousDomain = resolveDomainFromLocation(
                        this.agentStore.routeBeforeFullscreen,
                    );
                    activeDomain = previousDomain;
                }

                if (
                    activeDomain !== ActiveDomain.Conversations &&
                    activeDomain !== ActiveDomain.Signals
                ) {
                    return undefined;
                }

                const filters =
                    activeDomain === ActiveDomain.Conversations
                        ? this.getAppliedConversationFilters()
                        : (context.getValue() as ApplicationFilters);

                return {
                    contextType:
                        activeDomain === ActiveDomain.Conversations
                            ? "Conversations"
                            : "Signals",
                    filters: filters,
                };
            case UserSelectedContextType.ChartDataPoint:
                if (!this.chartDataPointContext) {
                    return undefined;
                }
                return {
                    contextType: "DataPoint",
                    filters: this.chartDataPointContext.filters,
                    chartId: this.chartDataPointContext.chartId,
                    chartTitle: this.chartDataPointContext.chartTitle,
                    endpointId: this.chartDataPointContext.endpointId,
                    contextSummary: this.chartDataPointContext.contextSummary,
                    dataPointQueryContext:
                        this.chartDataPointContext.dataPointQueryContext,
                };
            case UserSelectedContextType.ProactiveInsight:
                if (!this.proactiveInsightContext) {
                    return undefined;
                }
                return {
                    contextType: "ProactiveInsight",
                    filters: this.proactiveInsightContext.filters,
                    insightType: this.proactiveInsightContext.insightType,
                    insightTitle: this.proactiveInsightContext.insightTitle,
                    contextSummary: this.proactiveInsightContext.contextSummary,
                };
        }

        return undefined;
    }

    private getAppliedConversationFilters(): AgentConversationFilters {
        const applicationFiltersStore = this.rootStore.getStore(
            ApplicationFiltersStore,
        );

        const appliedFilters =
            applicationFiltersStore.currentlyAppliedFilters ??
            applicationFiltersStore.savedFilterStates["Conversations"] ??
            applicationFiltersStore.toFilterObject();

        const filters = prepareFiltersForAgentContext(
            appliedFilters,
        ) as AgentConversationFilters;

        if (this.conversationsStore.conversationIds?.length) {
            filters.conversationIds = this.conversationsStore.conversationIds;
        }

        return filters;
    }

    // Gets the current context the chat is in
    private getCurrentContext(): IAgentContext | undefined {
        switch (this.rootStore.activeLocation.domain) {
            case ActiveDomain.Conversation:
                return DomainBasedContext.SingleConversation;
            case ActiveDomain.Evaluation:
                return DomainBasedContext.Evaluation;
        }

        if (this.userSelectedContext) {
            return this.userSelectedContext;
        }

        return undefined;
    }

    // Submits user input to the backend to start streaming
    @action
    async streamingSubmitInput(options?: StreamingSubmitInputOptions) {
        if (!this.agentStore.canAccessKboRoutes) {
            return;
        }

        if (this.agentStore.shouldPaywallOnInteract) {
            this.agentStore.openPaywallDialog();
            return;
        }

        if (!this.agentStore.ensureUserAgreementAccepted()) {
            return;
        }

        this.clearEndMessage();

        const input = options?.manualInput ?? this.currentInput;
        this.currentInput = "";
        // If we aren't doing a server thing and there's no input just stop
        if (!input && !options?.serverAction) return;
        options ??= {};
        options.userMessage ??= input;

        // Commit any transient messages to the chat and grab the suggestions
        const recentAgentMessages = this.chat.recentAgentMessageGroup!;
        recentAgentMessages.commitTransientMessage();
        const suggestions = recentAgentMessages.suggestions;

        // Research chats arrive already started and named by KBo Flow.
        const isFirstUserMessage =
            !this.chat.hasUserMessaged && !this.chat.isKboFlowResearch;
        const isAppliedFilters =
            this.userSelectedContext?.type ===
            UserSelectedContextType.AppliedFilters;

        // Add the users message to the chat
        this.chat.addUserMessageGroup().addMessage(options.userMessage);
        this.agentStore.setIsEmpty(false);

        const shouldAskCreateChat = !this.chat.id || this.chat.id === emptyGUID;

        // Handle the streaming with the arrow function
        this.handleStreaming((chatId, abortSignal) => {
            // Handle streaming creates our chat id and abort signal
            // it then calls this arrow function to get a stream to handle

            // Set any additional info
            let additionalInformation: Record<string, unknown> | undefined =
                options?.additionalInformation;

            if (options?.suggestion) {
                additionalInformation ??= {};
                additionalInformation["Suggestion"] = true;
            }

            if (
                options?.userMessage &&
                (options.userMessage !== input || options.serverAction)
            ) {
                additionalInformation ??= {};
                additionalInformation["DisplayMessage"] = options.userMessage;
            }

            const onChatStarted = shouldAskCreateChat
                ? (startedChatId: string) => {
                      runInAction(() => {
                          this.attachStartedChat(startedChatId, "Generating...");
                      });
                      if (isFirstUserMessage) {
                          this.handleFirstUserMessageStarted(startedChatId, input);
                      }
                  }
                : undefined;

            if (isFirstUserMessage && !shouldAskCreateChat) {
                this.handleFirstUserMessageStarted(chatId, input);
            }

            if (isFirstUserMessage && isAppliedFilters) {
                // Applied filters message. Do not let this delayed update from
                // a cancelled request land in a newer response.
                setTimeout(() => {
                    if (
                        abortSignal.aborted ||
                        this.abortController?.signal !== abortSignal
                    ) {
                        return;
                    }

                    this.chat
                        .addAgentMessageGroup()
                        .addMessage(
                            "I'm analyzing, based on your applied filters.",
                        );
                }, 1000);
            }

            // Submit the message to the backend and return the stream it sends back to us
            return this.acxAgentService.streamingSubmitMessage(
                {
                    chatId: shouldAskCreateChat ? undefined : chatId,
                    body: input,
                    availableActions: suggestions
                        ?.filter((s) => !s.doNotSendToLlm && s.serverAction)
                        .map((s) => ({
                            suggestionText: s.content,
                            llmInput: s.llmInput,
                            serverAction: s.serverAction,
                        })),
                    serverAction: options?.serverAction,
                    similarClips: options?.similarClips,
                    kboSessionSettings: this.sessionSettings,
                },
                additionalInformation,
                this.getAgentContext(),
                abortSignal,
                onChatStarted,
            );
        });
    }

    /* Various actions KBo can do. These things are actions run on the chat, such as generating coaching notes or summaries. */

    private async generateCoachingNotesAction(data: GenerateCoachingNotesData) {
        runInAction(() => {
            this.conversationsStore.evalReviewStore.isGeneratingCoachingNotesFromKBo =
                true;
        });

        const messageGroup = this.chat.addAgentMessageGroup();
        try {
            await messageGroup
                .addMessage(
                    "I'll generate those for you now. Please note, our AI Coaching Notes are still in beta. Please review them for accuracy before submitting.",
                )
                .persist();
        } catch (error) {
            runInAction(() => {
                this.conversationsStore.evalReviewStore.isGeneratingCoachingNotesFromKBo =
                    false;
            });
            throw error;
        }

        this.state = AgentState.Waiting;

        let message: IChatMessage | undefined = undefined;
        let additionalInformation: Record<string, unknown> | undefined =
            undefined;

        this.setupAsyncTask(LoadingAICoachingNotesFromChat, async () => {
            try {
                this.agentStore.closeDrawer();
                const panelOpened =
                    await this.conversationsStore.openCoachingPanelWithLock({
                        refreshCoaching: true,
                    });

                if (
                    this.conversationsStore.currentAgentCoaching
                        ?.coachingStatus === AgentCoachingStatus.Completed
                ) {
                    message = messageGroup.addMessage(
                        panelOpened
                            ? "I've opened the saved coaching notes."
                            : "I couldn't open the saved coaching notes. Please try again.",
                    );
                    return;
                }

                if (!panelOpened) {
                    message = messageGroup.addMessage(
                        "I couldn't open the coaching side panel. Please try again.",
                    );
                    return;
                }

                const result = await this.acxAgentService.generateCoachingNotes(
                    data.agentId,
                    data.audioMetadataId,
                    this.abortController?.signal,
                );

                if (result.successful) {
                    const opportunities = result.response.opportunities ?? [];
                    const strengths = result.response.strengths ?? [];

                    // Format strengths into a bulleted list
                    const strengthsText = strengths.length > 0
                        ? strengths
                              .map(strength => `• ${strength}`)
                              .join('\n')
                        : 'No strengths were identified for this conversation.';

                    // Format opportunities with feedback and action items
                    const opportunitiesText = opportunities
                        .map((opp, idx) => {
                            return `**Opportunity ${idx + 1}:**\n` +
                                   `Feedback: ${opp.feedback}\n` +
                                   `Action: ${opp.actionItem}`;
                        })
                        .join('\n\n');

                    // Combine opportunities with summary
                    const improvementText = opportunitiesText +
                        (result.response.summary ? `\n\n**Summary:**\n${result.response.summary}` : '');

                    const notes = {
                        doesWellNotes: strengthsText,
                        improvementNotes: improvementText,
                    };

                    this.conversationsStore.evalReviewStore.hasGeneratedCoachingNotes = true;
                    this.conversationsStore.evalReviewStore?.agentCoaching?.setDoWellNotes(notes.doesWellNotes);
                    this.conversationsStore.evalReviewStore?.agentCoaching?.setImprovementNotes(
                        notes.improvementNotes,
                    );

                    message = messageGroup.addMessage(
                        "I've opened the coaching side panel with the generated coaching notes.",
                    );
                    additionalInformation = { CoachingNotes: notes };
                } else {
                    message = messageGroup.addMessage(
                        result.message.replaceAll(
                            "helpdesk@authenticx.com",
                            "[helpdesk@authenticx.com](mailto:helpdesk@authenticx.com)",
                        ),
                    );
                }
            } catch (error: unknown) {
                if(!this.abortController?.signal.aborted){
                    message = messageGroup.addMessage(
                        "An error occured while generating coaching notes. Please try again.",
                    );
                }
            } finally {
                if (message) {
                    message.persist(additionalInformation);
                }
                runInAction(() => {
                    this.conversationsStore.evalReviewStore.isGeneratingCoachingNotesFromKBo =
                        false;
                    this.state = AgentState.Idle;
                });
            }
        });
    }

    private extractGenerateCoachingNotesDataFromEvaluation(
        evaluation: Evaluation,
    ): GenerateCoachingNotesData | undefined {
        const interaction = evaluation.interaction;
        if (
            !interaction ||
            !interaction.agentId ||
            !interaction.agentName ||
            !interaction.audioMetadataId ||
            !!evaluation.agentCoachingId ||
            interaction.interactionType === InteractionType.Live
        ) {
            return undefined;
        }

        return {
            agentId: interaction.agentId,
            agentName: interaction.agentName,
            audioMetadataId: interaction.audioMetadataId,
        };
    }

    private extractGenerateCoachingNotesDataFromConversation(
        conversation: Conversation,
    ) {
        if (!!conversation.evaluationAgentCoachingId) {
            return undefined;
        }
        if (!conversation.agentId || conversation.agentId === emptyGUID) {
            return undefined;
        }
        if (!conversation.agentFirstName && !conversation.agentLastName) {
            return undefined;
        }

        return {
            agentId: conversation.agentId,
            agentName: `${conversation.agentFirstName} ${conversation.agentLastName}`,
            audioMetadataId: conversation.conversationId,
        };
    }

    public async analyzeCurrentConversationsAction(
        additionalInformation?: Record<string, unknown>,
    ) {
        this.setUserSelectedContext(UserSelectedContext.AppliedFilters);
        this.chat.recentAgentMessageGroup!.commitTransientMessage();

        const message = this.chat
            .addUserMessageGroup()
            .addMessage("Analyze and Surface Trends");
        this.agentStore.setIsEmpty(false);
        this.state = AgentState.Waiting;
        const abortSignal = this.abortController?.signal;
        let replyGroup;
        try {
            // The flag tells the server this chat is analyzing trends, so it saves a hint with this
            // message and makes the Analyze Trends skill available for the rest of the chat.
            await this.persistMessageOrStartChat(
                message,
                "Analyze Current Conversations",
                { ...additionalInformation, AnalyzeTrends: true },
            );
            // A canned reply, then the choices. No server call to KBo until a choice is made.
            replyGroup = await this.simulateStreamedAgentResponse(
                AnalyzeTrendsIntroText,
                { abortSignal },
            );
        } catch (error) {
            if (isAbortError(error)) return;
            if (isKboUserAgreementRequiredError(error)) {
                this.chat
                    .addAgentMessageGroup()
                    .addMessage(
                        "You need to accept the KBo User Agreement before KBo can answer, so this request wasn't sent.",
                    );
                void this.agentStore.handleUserAgreementRequired();
                return;
            }
            if (!this.abortController?.signal.aborted) {
                this.chat
                    .addAgentMessageGroup()
                    .addMessage(
                        "I was unable to complete your request. Please try again.",
                    );
            }
            return;
        } finally {
            runInAction(() => {
                this.state = AgentState.Idle;
            });
        }

        // Either choice, or anything the user types instead, runs as regular KBo chat;
        // the hint saved with the Analyze Trends message points KBo to the skill.
        replyGroup.setSuggestions([
            {
                content: AnalyzeTrendsOverviewText,
                action: (store, suggestion) =>
                    store.streamingSubmitInput({
                        manualInput: suggestion.content,
                        suggestion: true,
                    }),
            },
            {
                content: AnalyzeTrendsQuestionText,
                action: async (store, suggestion) => {
                    const abortSignal = store.abortController?.signal;
                    try {
                        await store.simulateUserMessageAndStreamedResponse(
                            suggestion.content,
                            "Awesome, tell me what you're looking for!",
                            { abortSignal },
                        );
                    } catch (error) {
                        if (!isAbortError(error)) throw error;
                        // A newer request owns the chat's state now; leave it alone
                        if (store.abortController?.signal !== abortSignal) return;
                        runInAction(() => {
                            store.activeStreamingMessage = undefined;
                            store.state = AgentState.Idle;
                        });
                    }
                },
            },
        ]);
    }

    public async analyzeDataPoint(
        countString: string,
        dataPointFilters?: ApplicationFilters,
        contextMetadata?: {
            chartId?: string;
            chartTitle?: string;
            endpointId?: string;
            contextSummary?: string;
            dataPointQueryContext?: DataPointQueryContext;
        },
    ) {
        if (countString.trim() === "") {
            this.chat
                .recentAgentMessageGroup!.addMessage(
                    "There are no conversations for this data point. Please try another data point.",
                )
                .persist();
            return;
        }

        if (!dataPointFilters) {
            // We should always have filters here, but just in case
            // If we don't have filters for the data point, then we can't analyze
            // that specific data point (we would just be doing analyze conversations at that point)
            this.chat
                .recentAgentMessageGroup!.addMessage(
                    "I was unable to analyze this data point. Please try again.",
                )
                .persist();
            return;
        }

        const preparedDataPointFilters =
            prepareFiltersForAgentContext(dataPointFilters);

        this.chartDataPointContext = {
            filters: preparedDataPointFilters,
            chartId: contextMetadata?.chartId,
            chartTitle: contextMetadata?.chartTitle,
            endpointId: contextMetadata?.endpointId,
            contextSummary: contextMetadata?.contextSummary,
            dataPointQueryContext: contextMetadata?.dataPointQueryContext,
        };
        this.setUserSelectedContext(UserSelectedContext.ChartDataPoint);
        const shouldCreateChat = !this.chat.id || this.chat.id === emptyGUID;
        const displayMessage =
            contextMetadata?.contextSummary?.trim() || "Analyze Data Point";

        this.handleStreaming(async (chatId, abortSignal) => {
            const MAX_NUMBER_OF_CONVERSATIONS = 50; //same as what we have in ConversationTrendEvaluationService.cs, see NUMBER_OF_TRANSCRIPTS_TO_SELECT

            const message =
                parseInt(countString) <= MAX_NUMBER_OF_CONVERSATIONS
                    ? `I'm analyzing all ${countString} conversations from the selected data point.`
                    : `I'm analyzing a sample set of these ${countString} conversations from the selected data point.`;

            await this.simulateUserMessageAndStreamedResponse(
                displayMessage,
                message,
                {
                    abortSignal,
                    persistMessages: !shouldCreateChat,
                },
            );
            this.state = AgentState.Waiting;

            return this.acxAgentService.analyzeDataPoint(
                shouldCreateChat ? undefined : chatId,
                {
                    contextType: "DataPoint",
                    filters: preparedDataPointFilters,
                    chartId: contextMetadata?.chartId,
                    chartTitle: contextMetadata?.chartTitle,
                    endpointId: contextMetadata?.endpointId,
                    contextSummary: contextMetadata?.contextSummary,
                    dataPointQueryContext:
                        contextMetadata?.dataPointQueryContext,
                },
                abortSignal,
                shouldCreateChat
                    ? this.createChatStartedHandler(displayMessage)
                    : undefined,
            );
        });
    }

    public async analyzeProactiveInsight(
        insightType: string,
        filterContext: {
            contextType: "ProactiveInsight";
            filters: ApplicationFilters;
            insightTitle: string;
            contextSummary: string;
        },
        analysisSubject: string,
    ) {
        const preparedInsightFilters =
            prepareFiltersForAgentContext(filterContext.filters);

        this.proactiveInsightContext = {
            filters: preparedInsightFilters,
            insightType,
            insightTitle: filterContext.insightTitle,
            contextSummary: filterContext.contextSummary,
        };
        this.setUserSelectedContext(UserSelectedContext.ProactiveInsight);
        const shouldCreateChat = !this.chat.id || this.chat.id === emptyGUID;

        this.handleStreaming(async (chatId, abortSignal) => {
            const userMessage = this.chat
                .addUserMessageGroup()
                .addMessage(analysisSubject);
            if (!shouldCreateChat) {
                await userMessage.persist();
            }
            this.state = AgentState.Waiting;

            return this.acxAgentService.analyzeProactiveInsight(
                shouldCreateChat ? undefined : chatId,
                insightType,
                {
                    contextType: "ProactiveInsight",
                    filters: preparedInsightFilters,
                    insightType,
                    insightTitle: filterContext.insightTitle,
                    contextSummary: filterContext.contextSummary,
                },
                analysisSubject,
                abortSignal,
                shouldCreateChat
                    ? this.createChatStartedHandler(analysisSubject)
                    : undefined,
            );
        });
    }

    public async analyzeModelResultsAction(
        llmClassifier: { id: string; name: string },
        result?: string,
    ) {
        this.agentStore.openDrawer();

        let message: string;
        if (result) {
            message = `Analyze ${result} results for ${llmClassifier.name}`;
        } else {
            message = `Analyze all results for ${llmClassifier.name}`;
        }

        this.setUserSelectedContext(UserSelectedContext.AppliedFilters);

        this.streamingSubmitInput({
            manualInput: message,
            serverAction: {
                type: "ForceToolCall",
                arguments: {
                    toolCall: ForceableToolCall.AnalyzeModelResults
                }
            }
        });
    }

    // Has KBo gather clips similar to the selected clip and build them into a montage it plays in the chat.
    // Montage Builder stays one click away through the suggestion KBo adds with the clips.
    private createSimilarClipsSuggestion(
        additionalClips: number,
    ): ChatSuggestion {
        return {
            content: `${additionalClips} Additional Clips`,
            action: async () => {
                await this.streamingSubmitInput({
                    manualInput: `${additionalClips} Additional Clips`,
                    similarClips: {
                        soundClipId: this.clipForMontageBuildRequest!.id,
                        additionalClips,
                    },
                });
            },
            newChatName: "Montage Request from KBo",
        };
    }

    public async prepareBuildMontage(data: BuildMontageFromClipsData) {
        const montageStore = this.rootStore.getStore(MontageSoundClipStore);
        montageStore.datePickerStore.setBeginDate(
            moment(data.minClipDate).subtract(1, "days"),
        );
        montageStore.datePickerStore.setEndDate(
            moment(data.maxClipDate).add(1, "days"),
        );
        montageStore.resetAll();
        await montageStore.getAllClips(
            montageStore.datePickerStore.beginDate,
            montageStore.datePickerStore.endDate,
            "",
        );
        montageStore.suppressRefresh = true;
        montageStore.tabIndex = 1;

        montageStore.soundClipDgStore.handleSelectionChange(data.clipIds);
    }

    // Loads a montage KBo created into Montage Builder for editing
    public async prepareEditMontage(montageId: string, name: string) {
        // The builder's edit and save path only reads the montage's id and name
        const montage = new MontageModel();
        montage.id = montageId;
        montage.name = name;

        await this.rootStore
            .getStore(MontageListStore)
            .openMontageInBuilder(montage);

        // Keeps the builder from reloading its clips when Montage Library mounts. Set only once the montage is
        // loaded, since a failed load doesn't navigate there to clear it.
        this.rootStore.getStore(MontageSoundClipStore).suppressRefresh = true;
    }

    // These are for the buttons in the platform that open the sidebar for an action
    // They open the sidebar to ask a follow up like "how many x do you want"
    // The suggestions/action buttons spawned by that trigger one of the above action functions

    @action
    async showAgentBuildMontageForClip(soundClip: SoundClip) {
        if (!this.agentStore.ensureUserAgreementAccepted()) return;

        this.clipForMontageBuildRequest = soundClip;

        const recentAgentMessageGroup = this.chat.recentAgentMessageGroup!;
        recentAgentMessageGroup.clearTransientMessage();

        this.agentStore.openDrawer();

        recentAgentMessageGroup.setTransientMessageTemplated(
            this.clipForMontageBuildRequest,
            (soundClip: unknown) => {
                if (soundClip instanceof SoundClip) {
                    return (
                        "Got it! I can help you create a montage with clips similar to " +
                        (soundClip.segmentName &&
                        soundClip.segmentName.trim() !== ""
                            ? "the clip named: " + soundClip.segmentName
                            : "the un-named clip with duration: " +
                              soundClipDurationDisplay(soundClip.startTime) +
                              " - " +
                              soundClipDurationDisplay(soundClip.endTime)) +
                        ". \n \n How many additional soundclips would you like in the montage?"
                    );
                }
                return "Invalid soundclip! Please select another soundclip.";
            },
            [
                this.createSimilarClipsSuggestion(10),
                this.createSimilarClipsSuggestion(5),
                this.createSimilarClipsSuggestion(3),
            ],
        );
    }

    @action
    async showFindSimilarConversations() {
        if (!this.agentStore.ensureUserAgreementAccepted()) return;

        const recentAgentMessageGroup = this.chat.recentAgentMessageGroup!;
        recentAgentMessageGroup.clearTransientMessage();

        this.agentStore.openDrawer();

        const action: ChatSuggestAction = async (store, suggestion) => {
            await store.streamingSubmitInput({
                manualInput: suggestion.content,
                serverAction: {
                    type: "ForceToolCall",
                    arguments: {
                        toolCall: ForceableToolCall.FindSimilarConversations
                    }
                }
            });
        };

        recentAgentMessageGroup.setTransientMessage(
            `Got it! I can help you find conversations similar to this one.\n\n How many similar conversations would you like?`,
            [
                {
                    content: "Up to 3 Similar Conversations",
                    action,
                    newChatName: "Find Similar Conversations",
                },
                {
                    content: "Up to 5 Similar Conversations",
                    action,
                    newChatName: "Find Similar Conversations",
                },
                {
                    content: "Up to 10 Similar Conversations",
                    action,
                    newChatName: "Find Similar Conversations",
                },
            ],
        );
    }

    // Submit an action to the server
    private submitServerAction(
        input: string,
        serverAction: ChatSuggestionServerAction,
    ) {
        this.streamingSubmitInput({
            manualInput: input,
            serverAction,
            userMessage: input,
        });
    }

    /**
     * Simulates a streaming response without making any server calls.
     * Useful for demos, testing, or showing loading states.
     * @param userMessage The message to display as the user's input
     * @param responseText The text to stream as the agent's response
     * @param options
     */
    @action
    async simulateUserMessageAndStreamedResponse(
        userMessage: string,
        responseText: string,
        options?: {
            actions?: ChatActionsMap;
            loadingDelayMs?: number;
            tokenDelayMs?: number;
            abortSignal?: AbortSignal;
            persistMessages?: boolean;
        },
    ) {
        this.clearEndMessage();

        const persistMessages = options?.persistMessages ?? true;

        // Add user message
        const chat = this.chat.addUserMessageGroup().addMessage(userMessage);

        if (persistMessages) {
            await chat.persist();
        }
        throwIfAborted(options?.abortSignal);

        await this.simulateStreamedAgentResponse(responseText, options);
    }

    /**
     * Streams a canned agent reply without making any server calls.
     * @returns The agent message group holding the reply
     */
    @action
    private async simulateStreamedAgentResponse(
        responseText: string,
        options?: {
            actions?: ChatActionsMap;
            loadingDelayMs?: number;
            tokenDelayMs?: number;
            abortSignal?: AbortSignal;
            persistMessages?: boolean;
        },
    ) {
        const actions = options?.actions;
        const loadingDelayMs = options?.loadingDelayMs ?? 2000;
        const tokenDelayMs = options?.tokenDelayMs ?? 50;
        const abortSignal = options?.abortSignal;
        const persistMessages = options?.persistMessages ?? true;

        // Simulate loading state
        this.state = AgentState.Waiting;
        await abortableDelay(loadingDelayMs, abortSignal);

        // Create agent message group
        const agentMessageGroup = this.chat.addAgentMessageGroup();
        agentMessageGroup.clearSuggestions();
        const message = agentMessageGroup.addMessage("") as ChatMessage;

        // Start streaming simulation
        this.state = AgentState.Streaming;
        if (this.abortController?.signal === abortSignal) {
            this.activeStreamingMessage = message;
        }

        // Stream the response one token at a time
        const tokens = responseText.split(" ");
        for (let i = 0; i < tokens.length; i++) {
            if (i > 0) message.appendContent(" ");
            message.appendContent(tokens[i]);
            // Small delay between tokens to simulate streaming
            await abortableDelay(tokenDelayMs, abortSignal);
        }

        if (actions) {
            agentMessageGroup.setActions(actions);
        }
        if (persistMessages) {
            await message.persist();
        }
        throwIfAborted(abortSignal);

        // Set back to idle
        if (this.abortController?.signal === abortSignal) {
            this.activeStreamingMessage = undefined;
        }
        this.state = AgentState.Idle;
        return agentMessageGroup;
    }

    @action
    async clearAgentAnalysisState() {
        await this.acxAgentService.clearAgentAnalysisState(this.chat.id);
        this.chartDataPointContext = undefined;
        this.proactiveInsightContext = undefined;

        if (
            this.userSelectedContext?.type ===
                UserSelectedContextType.ChartDataPoint ||
            this.userSelectedContext?.type ===
                UserSelectedContextType.ProactiveInsight
        ) {
            this.setUserSelectedContext(UserSelectedContext.AppliedFilters);
        }
    }
}

function createAbortError() {
    return new DOMException("The request was aborted.", "AbortError");
}

function throwIfAborted(abortSignal?: AbortSignal) {
    if (abortSignal?.aborted) {
        throw createAbortError();
    }
}

function abortableDelay(milliseconds: number, abortSignal?: AbortSignal) {
    if (!abortSignal) {
        return new Promise<void>((resolve) =>
            setTimeout(resolve, milliseconds),
        );
    }

    throwIfAborted(abortSignal);

    return new Promise<void>((resolve, reject) => {
        const timeoutHandle = setTimeout(() => {
            abortSignal.removeEventListener("abort", handleAbort);
            resolve();
        }, milliseconds);
        const handleAbort = () => {
            clearTimeout(timeoutHandle);
            reject(createAbortError());
        };

        abortSignal.addEventListener("abort", handleAbort, { once: true });
    });
}

function isAbortError(error: unknown) {
    return error instanceof Error && error.name === "AbortError";
}

/**
 * The streaming endpoint serializes with Newtonsoft camelCase, but
 * FunctionCallsModal expects the PascalCase shape from the DB.
 * This maps the camelCase stream data to the PascalCase FunctionCall type.
 */
function normalizeFunctionCalls(raw: Record<string, unknown>[]): FunctionCall[] {
    return raw.map((c: Record<string, unknown>) => {
        const fn = (c.Function ?? c.function ?? {}) as Record<string, unknown>;
        const meta = (fn.Metadata ?? fn.metadata ?? {}) as Record<string, unknown>;
        const params = ((meta.Parameters ?? meta.parameters) as Record<string, unknown>[] | undefined);
        return {
            Function: {
                Name: (fn.Name ?? fn.name ?? "") as string,
                PluginName: (fn.PluginName ?? fn.pluginName ?? "") as string,
                Description: (fn.Description ?? fn.description ?? "") as string,
                Metadata: {
                    Name: (meta.Name ?? meta.name ?? "") as string,
                    PluginName: (meta.PluginName ?? meta.pluginName ?? "") as string,
                    Description: (meta.Description ?? meta.description ?? "") as string,
                    Parameters: params?.map((p: Record<string, unknown>) => ({
                        Name: (p.Name ?? p.name ?? "") as string,
                        IsRequired: (p.IsRequired ?? p.isRequired ?? false) as boolean,
                        Description: (p.Description ?? p.description ?? "") as string,
                        ParameterType: (p.ParameterType ?? p.parameterType ?? "") as string,
                    })),
                },
            },
            Arguments: (c.Arguments ?? c.arguments ?? {}) as Record<string, unknown>,
            Exception: (c.Exception ?? c.exception) as string | undefined,
        };
    });
}
